import React, { useEffect, useRef, useState } from "react";
import { Form, Button } from "react-bootstrap";
import {
  FiArrowRight,
  FiCheckCircle,
  FiCornerDownRight,
} from "react-icons/fi";

export default function LogicPicker({
  value,
  onChange,
  questions = [],
  currentQuestionId,
  endPages = [],
  onCreateEndPage,
}) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState("root");
  const ref = useRef(null);

  const [submitMode, setSubmitMode] = useState("existing");
  const [selectedEndId, setSelectedEndId] = useState(null);
  const [newEndName, setNewEndName] = useState(""); 
  const [newEndDesc, setNewEndDesc] = useState("");
  const nameLimit = 40;

  const PURPLE = "#2E2868";
  const submitPanelActive = panel === "submit";

  const inputRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (open && ref.current && !ref.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open || panel !== "submit") return;

    if (value?.action === "submit" && value?.endPageId) {
      setSubmitMode("existing");
      setSelectedEndId(String(value.endPageId));
      setNewEndName("");
    } else if (value?.action === "submit" && value?.newEndPageName) {
      setSubmitMode("create");
      setNewEndName(value.newEndPageName);
      setSelectedEndId(null);
    } else {
      const first = endPages[0];
      setSubmitMode(endPages.length ? "existing" : "create");
      setSelectedEndId(first ? String(first.id) : null);
      setNewEndName("");
    }
  }, [open, panel, value, endPages]);

  useEffect(() => {
    if (panel === "submit" && submitMode === "create" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [panel, submitMode]);

  const label = !value
    ? "Proceed"
    : value.action === "goto"
    ? "Go to follow-up question"
    : value.action === "submit"
    ? "End questionnaire"
    : "Proceed";

  const handleRootPick = (action) => {
    if (action === "proceed") {
      onChange(null);
      setOpen(false);
      return;
    }

    if (action === "goto") {
      setPanel("goto");
      onChange({
        action: "goto",
        targetId: value?.action === "goto" ? value.targetId : null,
      });
      return;
    }

    if (action === "submit") {
      setPanel("submit");
      if (value?.action !== "submit") onChange({ action: "submit" });

      if (!endPages.length) {
        setSubmitMode("create");
        setSelectedEndId(null);
      } else if (submitMode !== "existing" && submitMode !== "create") {
        const first = endPages[0];
        setSubmitMode("existing");
        setSelectedEndId(first ? String(first.id) : null);
      }
      return;
    }
    setOpen(false);
  };

  const questionsToShow = (questions || []).filter(
    (q) => q.id !== currentQuestionId,
  );

  const pickTarget = (id) => {
    onChange({ action: "goto", targetId: id });
    setOpen(false);
  };

  const selectExisting = (epId) => {
    const idStr = String(epId);
    setSubmitMode("existing");
    setSelectedEndId(idStr);
  };

  const onSubmitChoiceChange = (e) => {
    const v = e.target.value;
    if (v === "__create__") {
      setSubmitMode("create");
      setSelectedEndId(null);
      setNewEndName(""); 
    } else {
      selectExisting(v);
    }
  };

  const confirmSubmitExisting = () => {
    if (!selectedEndId) return;
    onChange({ action: "submit", endPageId: selectedEndId });
    setOpen(false);
  };

  const confirmSubmitCreate = async () => {
    const name = (newEndName || "").trim();
    const desc = (newEndDesc || "").trim(); 
    if (!name) return;

    if (typeof onCreateEndPage === "function") {
      try {
        const created = await onCreateEndPage(name, desc);
        if (created?.id) {
          onChange({ action: "submit", endPageId: String(created.id) });
          setOpen(false);
          return;
        }
      } catch (err) {}
    }

    onChange({ action: "submit", newEndPageName: name, newEndPageDesc: desc });
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => {
          setOpen((s) => !s);
          setPanel("root");
        }}
        style={{
          borderRadius: 20,
          border: "1px solid #ccc",
          background: "#fff",
          padding: "6px 12px",
          minWidth: 160,
          textAlign: "left",
          fontSize: 13,
          color: !value ? "#6c757d" : "#2E2868",
          fontWeight: !value ? 400 : 600,
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {label}
        <span style={{ float: "right", marginLeft: 8 }}>▾</span>
      </button>

      {open && (
        <div
          id="logic-picker-popover"
          style={{
            position: "absolute",
            top: "110%",
            left: 0,
            display: "flex",
            gap: 8,
            zIndex: 1000,
          }}
        >
          {/* ROOT PANEL */}
          <div
            style={{
              width: 280,
              background: "#fff",
              border: "1px solid #dcddea",
              borderRadius: 12,
              padding: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            }}
          >
            <div
              onClick={() => handleRootPick("proceed")}
              style={{
                padding: 10,
                borderRadius: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <FiArrowRight
                size={18}
                color="#28a745"
                style={{ marginTop: 2 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Proceed</div>
                <div
                  style={{ fontSize: 12, color: "#6c757d", lineHeight: 1.3 }}
                >
                  Continue to the next question in order.
                </div>
              </div>
            </div>

            <div
              onClick={() => handleRootPick("goto")}
              style={{
                padding: 10,
                borderRadius: 10,
                cursor: "pointer",
                background: panel === "goto" ? "#f1f0fb" : "transparent",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <FiCornerDownRight
                size={18}
                color="#5b5b75"
                style={{ marginTop: 2 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  Go to follow-up question
                </div>
                <div
                  style={{ fontSize: 12, color: "#6c757d", lineHeight: 1.3 }}
                >
                  Jump to a specific question based on this answer.
                </div>
              </div>
              <span style={{ marginLeft: "auto", color: "#6c757d" }}>›</span>
            </div>

            <div
              onClick={() => handleRootPick("submit")}
              style={{
                padding: 10,
                borderRadius: 10,
                cursor: "pointer",
                background: panel === "submit" ? "#f1f0fb" : "transparent",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <FiCheckCircle
                size={18}
                color="#5b5b75"
                style={{ marginTop: 2 }}
              />
              <div>
                <div
                  style={{ fontWeight: 600, fontSize: 14, color: "#1c1c28" }}
                >
                  End questionnaire
                </div>
                <div
                  style={{ fontSize: 12, color: "#6c757d", lineHeight: 1.3 }}
                >
                  Stop and submit the form immediately.
                </div>
              </div>
              <span style={{ marginLeft: "auto", color: "#6c757d" }}>›</span>
            </div>
          </div>

          {/* GOTO PANEL */}
          {panel === "goto" && (
            <div
              style={{
                width: 300,
                background: "#fff",
                border: "1px solid #dcddea",
                borderRadius: 12,
                padding: 10,
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                maxHeight: "350px",
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: 8,
                  flexShrink: 0,
                  fontSize: 13,
                  color: "#6c757d",
                }}
              >
                Select a follow-up question
              </div>

              <div
                style={{
                  overflowY: "auto",
                  display: "grid",
                  gap: 6,
                  paddingRight: 4,
                }}
              >
                {questionsToShow.length === 0 && (
                  <div style={{ fontSize: 12, color: "#6c757d", padding: 8 }}>
                    No other questions available.
                  </div>
                )}

                {questionsToShow.map((qq) => {
                  const realIndex = questions.findIndex((q) => q.id === qq.id);
                  return (
                    <label
                      key={qq.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 8px",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 13,
                        background:
                          value?.targetId === qq.id ? "#f1f0fb" : "transparent",
                      }}
                      onMouseEnter={(e) =>
                        !value?.targetId &&
                        (e.currentTarget.style.background = "#f8f9fa")
                      }
                      onMouseLeave={(e) =>
                        !value?.targetId &&
                        (e.currentTarget.style.background = "transparent")
                      }
                      onClick={() => pickTarget(qq.id)}
                    >
                      <Form.Check
                        type="radio"
                        name="goto-target"
                        checked={
                          value?.action === "goto" && value?.targetId === qq.id
                        }
                        readOnly
                        style={{ margin: 0 }}
                      />
                      <div style={{ fontWeight: 500 }}>
                        {`Q${realIndex + 1}: ${qq.label || "Untitled"}`}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* SUBMIT (END) PANEL */}
          {panel === "submit" && (
            <div
              style={{
                width: 320,
                background: "#fff",
                border: submitPanelActive
                  ? `2px solid ${PURPLE}`
                  : "1px solid #dcddea",
                borderRadius: 12,
                padding: 16,
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                maxHeight: "350px", 
                overflowY: "auto"
              }}
            >
              <div
                style={{
                  marginBottom: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#555",
                }}
              >
                Select an End Page
              </div>

              {/* Existing End Pages */}
              <div
                style={{
                  display: "grid",
                  gap: 8,
                }}
              >
                {(endPages.length
                  ? endPages
                  : [{ id: "default", name: "Default End Page" }]
                ).map((ep) => (
                  <label
                    key={ep.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      padding: 6,
                      borderRadius: 6,
                      background:
                        selectedEndId === String(ep.id)
                          ? "#f0f0ff"
                          : "transparent",
                    }}
                    onClick={() => selectExisting(ep.id)}
                  >
                    <Form.Check
                      type="radio"
                      name="submit-choice"
                      value={String(ep.id)}
                      checked={
                        submitMode === "existing" &&
                        selectedEndId === String(ep.id)
                      }
                      onChange={onSubmitChoiceChange}
                      style={{ margin: 0 }}
                    />
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {ep.name}
                    </div>
                  </label>
                ))}

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    padding: 6,
                    borderRadius: 6,
                    marginTop: 4,
                    borderTop: "1px solid #eee",
                  }}
                  onClick={() => {
                    setSubmitMode("create");
                    setSelectedEndId(null);
                    setNewEndName("");
                  }}
                >
                  <Form.Check
                    type="radio"
                    name="submit-choice"
                    value="__create__"
                    checked={submitMode === "create"}
                    onChange={onSubmitChoiceChange}
                    style={{ margin: 0 }}
                  />
                  <div style={{ fontSize: 13, fontWeight: 600, color: PURPLE }}>
                    + Create new end page
                  </div>
                </label>
              </div>

              {submitMode === "create" && (
                <div
                  style={{
                    marginTop: 12,
                    borderTop: "1px solid #eee",
                    paddingTop: 10,
                  }}
                >
                  <div
                    style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}
                  >
                    Outcome 
                  </div>
                  <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                    <Form.Check
                      type="radio"
                      ref={inputRef} 
                      id="outcome-eligible"
                      label="Eligible"
                      name="outcome-type"
                      checked={newEndName === "Eligible"}
                      onChange={() => setNewEndName("Eligible")}
                      style={{ fontSize: 12, cursor: "pointer" }}
                    />
                    <Form.Check
                      type="radio"
                      id="outcome-ineligible"
                      label="Ineligible"
                      name="outcome-type"
                      checked={newEndName === "Ineligible"}
                      onChange={() => setNewEndName("Ineligible")}
                      style={{ fontSize: 12, cursor: "pointer" }}
                    />
                    <Form.Check
                      type="radio"
                      id="outcome-complete"
                      label="Complete"
                      name="outcome-type"
                      checked={newEndName === "Complete"}
                      onChange={() => setNewEndName("Complete")}
                      style={{ fontSize: 12, cursor: "pointer" }}
                    />
                  </div>

                  <div
                    style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}
                  >
                    Outro Message 
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="e.g. Unfortunately, based on your answers..."
                    value={newEndDesc}
                    size="sm"
                    onChange={(e) => setNewEndDesc(e.target.value)}
                    style={{ resize: "none" }}
                  />

                  <Button
                    size="sm"
                    style={{
                      marginTop: 8,
                      width: "100%",
                      background: PURPLE,
                      border: "none",
                    }}
                    disabled={!newEndName.trim()}
                    onClick={confirmSubmitCreate}
                  >
                    Create End Page
                  </Button>
                </div>
              )}

              {submitMode === "existing" && (
                <div style={{ marginTop: 12 }}>
                  <Button
                    size="sm"
                    style={{
                      width: "100%",
                      background: PURPLE,
                      border: "none",
                    }}
                    disabled={!selectedEndId}
                    onClick={confirmSubmitExisting}
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}